import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import escapeRegExp from 'lodash/escapeRegExp';
import { FarmOptionsTypes } from '../components/CustomizeColumns/types';
import {
  AdvancedSettingsEnum,
  SettingsFilterStringType,
  SettingsTypes,
} from '../containers/SettingsPage/types';
import { getFormattedDateText } from '../containers/SettingsPage/utils';
import { entityInfoSelectors } from './entityInfoSlice';
import { settingsOptionsSelectors } from './entitySettingsOptionsSlice';
import { selectColumns } from './tableColumnsSlice';
import { RootState } from './rootReducer';

export const entitySettingsInitialState: SettingsTypes.EntitySettingsInitialState =
  {
    filterSetting: SettingsFilterStringType.None,
    searchText: '',
  };

/** User settings are always visible; other roles only when selected in advanced. */
const isHiddenByAdvancedFilter = (
  settingRole: AdvancedSettingsEnum,
  advancedSettings: AdvancedSettingsEnum[],
) => {
  if (settingRole === AdvancedSettingsEnum.User) {
    return false;
  }

  return !advancedSettings.includes(settingRole);
};

export const entitySettingsSlice = createSlice({
  name: 'entitySettings',
  initialState: entitySettingsInitialState,
  reducers: {
    setFilterSetting: (state, action: PayloadAction<string>) => {
      state.filterSetting = action.payload;
    },
    setSettingsSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    clearEntitySettings: () => entitySettingsInitialState,
  },
});

export const { setFilterSetting, setSettingsSearchText, clearEntitySettings } =
  entitySettingsSlice.actions;

export const entitySettingsSelectors = {
  selectFilterString: (state: RootState) => state.entitySettings.filterSetting,
  selectSettingsSearchText: (state: RootState) =>
    state.entitySettings.searchText,
};

export const selectFilteredSettings = createSelector(
  (state: RootState, entitySettings: SettingsTypes.EntitySettingType[]) =>
    entitySettings,
  settingsOptionsSelectors.selectAdvancedSettings,
  entitySettingsSelectors.selectFilterString,
  entitySettingsSelectors.selectSettingsSearchText,
  selectColumns,
  settingsOptionsSelectors.selectFilteredAstronautOptions,
  entityInfoSelectors.selectFarmTimeZone,
  (
    entitySettings,
    advancedSettings,
    filterString,
    searchText,
    dynamicColumns,
    filteredAstronautOptions,
    farmTimeZone,
  ) => {
    const searchableColumns = dynamicColumns ?? [];
    const filteredSettings = entitySettings.filter((singleSetting) => {
      // filter default settings if filter is "hide default"
      if (
        filterString === SettingsFilterStringType.Default &&
        singleSetting.default
      ) {
        return false;
      }

      if (
        isHiddenByAdvancedFilter(singleSetting.settingRole, advancedSettings)
      ) {
        return false;
      }

      // filter identical values if filter is "hide identical values astronaut-{number}"
      const chosenIdenticalColumn =
        singleSetting.dynamicColumns &&
        singleSetting.dynamicColumns.find((astronaut) => {
          return (
            astronaut.columnName.toLowerCase() === filterString.toLowerCase()
          );
        });

      const searchTextMatch = getEscapedSettingMatch(
        singleSetting,
        searchText,
        searchableColumns,
        filteredAstronautOptions,
        farmTimeZone,
      );
      if (chosenIdenticalColumn) {
        return !chosenIdenticalColumn.isIdentical && !!searchTextMatch;
      }

      return searchTextMatch;
    });

    return filteredSettings;
  },
);

export const getEscapedSettingMatch = (
  setting: SettingsTypes.EntitySettingType,
  searchText: string,
  dynamicColumns: FarmOptionsTypes.Selection[],
  filteredAstronautOptions: SettingsTypes.FarmAstronaut[],
  farmTimeZone: string,
) => {
  const escapedSearchText = escapeRegExp(searchText);

  if (escapedSearchText === '') {
    return true;
  }

  // Combine all searchable columns: current entity value + basic columns + astronaut columns
  const allSearchableColumns: FarmOptionsTypes.Selection[] = [
    ...dynamicColumns,
    {
      key: 'value',
      name: 'value',
      searchable: true,
    },
    ...filteredAstronautOptions.map((option) => ({
      key: option.name?.toLowerCase() || '',
      name: option.name || '',
      searchable: true,
    })),
  ];

  const isAnyMatching = allSearchableColumns.find((col) => {
    if (!col.searchable) return false;

    let columnValue: string | undefined;
    columnValue = setting[col.key]?.toString();

    if (col.key === 'dateChanged' && columnValue) {
      const parsedDate = new Date(columnValue);
      if (!Number.isNaN(parsedDate.getTime())) {
        columnValue = getFormattedDateText(columnValue, farmTimeZone);
      }
    }

    if (col.key === 'settingRole' && columnValue === 'None') {
      columnValue = '-';
    }

    if (!columnValue && setting.dynamicColumns) {
      const dynamicCol = setting.dynamicColumns.find(
        (dynCol) => dynCol.columnName?.toLowerCase() === col.key,
      );
      columnValue = dynamicCol?.value?.toString();
    }

    return columnValue?.toLowerCase().match(escapedSearchText.toLowerCase());
  });

  return isAnyMatching;
};

export default entitySettingsSlice.reducer;
